import { useState } from 'react';

import { Feedback, Variation } from '@cartwhl/common/types';
import Loading from '@cartwhl/ui/atoms/Loading';
import DownvoteFullIcon from '@cartwhl/ui/icons/DownvoteFullIcon';
import DownvoteIcon from '@cartwhl/ui/icons/DownvoteIcon';
import UpvoteFullIcon from '@cartwhl/ui/icons/UpvoteFullIcon';
import UpvoteIcon from '@cartwhl/ui/icons/UpvoteIcon';
import Button from '@cartwhl/ui/input/Button';
import Heart from '@cartwhl/ui/messaging/Heart';

import { navigationStore, noticesStore, projectStore } from 'stores';

export default function CurrenMotionFeedback({
  className,
  variation,
  jobID,
  variationNumber,
  onFavoriteToggle,
}: {
  className?: string;
  variation: Variation;
  jobID: string;
  variationNumber: number;
  onFavoriteToggle?: (animationNumber: string, favorite: boolean) => void;
}) {
  const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);

  const handleFavoriteClick = async () => {
    if (isLoadingFavorite) return;

    if (!jobID) {
      noticesStore.error('Sorry, you cannot favorite the default project.');
      return;
    }

    const newFavoriteValue = !variation.favorite;
    setIsLoadingFavorite(true);
    const result = await projectStore.setFavorite(
      jobID,
      variationNumber,
      newFavoriteValue,
    );
    if (result.success) {
      onFavoriteToggle?.(String(variationNumber), newFavoriteValue);
    }
    setIsLoadingFavorite(false);
  };

  const handleFeedbackClick = (sentiment: 'positive' | 'negative') => {
    const payload: Feedback = {
      jobID,
      animationNumber: String(variationNumber),
      sentiment,
      mp4Path: variation.mp4Path,
      prompt: variation.prompt,
    };
    navigationStore.setFeedbackModal(payload);
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button
        variant="tertiary"
        icon={
          isLoadingFavorite ? (
            <Loading className="text-gray-60 h-4 w-4" />
          ) : (
            <Heart value={variation.favorite} />
          )
        }
        onClick={handleFavoriteClick}
      />
      <div className="flex items-center">
        <Button
          variant="tertiary"
          icon={
            variation.feedback === 'positive' ? (
              <UpvoteFullIcon />
            ) : (
              <UpvoteIcon />
            )
          }
          disabled={Boolean(variation.feedback)}
          onClick={() => handleFeedbackClick('positive')}
        />
        <Button
          variant="tertiary"
          icon={
            variation.feedback === 'negative' ? (
              <DownvoteFullIcon />
            ) : (
              <DownvoteIcon />
            )
          }
          disabled={Boolean(variation.feedback)}
          onClick={() => handleFeedbackClick('negative')}
        />
      </div>
    </div>
  );
}
